import * as Common from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  createColumnHelper,
  DataTable,
  Empty,
  type PaginationOptions,
  type RowSelectionOptions,
} from '@piccolohealth/ui';
import React from 'react';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { TextLink } from '../../components/generic/TextLink';
import { useAppContext } from '../../hooks/useAppContext';
import { ManageParticipantLabelsControl } from '../labels/ManageParticipantLabelsControl';
import { ParticipantActionMenu } from './ParticipantActionMenu';
import { ParticipantStatusTag } from './ParticipantStatusTag';

interface Props {
  isLoading: boolean;
  error: Common.PiccoloError | null;
  participants: Common.Participant[];
  refetch: () => Promise<void>;
  pagination?: PaginationOptions;
  rowSelection?: RowSelectionOptions<Common.Participant>;
}

export const ParticipantsTable = (props: Props) => {
  const { organization } = useAppContext();

  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<Common.Participant>();

    return [
      columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
        id: 'name',
        header: 'Name',
        minSize: 240,
        maxSize: 300,
        cell: (props) => (
          <ChakraV3.Stack gap={1}>
            <TextLink
              to={`/organizations/${organization.id}/participants/${props.row.original.id}`}
              fontWeight='bold'
              color='purple.600'
              fontSize='md'
            >
              {Common.renderParticipantName(props.row.original)}
            </TextLink>
            <ChakraV3.Text fontSize='xs'>{props.row.original.ndisNumber}</ChakraV3.Text>
          </ChakraV3.Stack>
        ),
      }),
      columnHelper.accessor('location.name', {
        header: 'Location',
        minSize: 200,
        maxSize: 240,
        cell: (props) => (
          <ChakraV3.Tag size='sm' colorPalette={props.row.original.location.color}>
            {props.row.original.location.name}
          </ChakraV3.Tag>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        minSize: 100,
        maxSize: 100,
        cell: (props) => <ParticipantStatusTag status={props.row.original.status} size='sm' />,
      }),
      columnHelper.accessor('label.name', {
        header: 'Labels',
        cell: (props) => (
          <ManageParticipantLabelsControl
            participantId={props.row.original.id}
            value={props.row.original.labels.map((l) => l.id)}
          />
        ),
      }),
      columnHelper.display({
        id: 'actions',
        maxSize: 140,
        header: 'Actions',
        cell: (props) => (
          <ParticipantActionMenu
            participantId={props.row.original.id}
            participantName={Common.renderParticipantName(props.row.original)}
            isDisabled={Common.isParticipantDisabled(props.row.original.status)}
            size='sm'
          />
        ),
      }),
    ];
  }, [organization.id]);

  return (
    <DataTable
      columns={columns}
      data={props.participants}
      size='md'
      error={props.error}
      isLoading={props.isLoading}
      pagination={props.pagination}
      rowSelection={props.rowSelection}
      renderError={(error) => <PiccoloError error={error} />}
      renderEmpty={() => <Empty title='No participants found' />}
    />
  );
};
